import { CommonModule } from '@angular/common';
import { Component, HostListener, Inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import {
  RebookAreaOption,
  RebookBlockOption,
  RebookShowOption,
  RebookTableOption,
  ReservationItem
} from '../../core/models/show.model';

export interface ReservationRebookDialogResult {
  processId: string;
  showId: number;
  areaId: string;
  slot: number;
  reservedCount: number;
  targetPlaceType: number;
  targetTableNumber: number;
}

interface ReservationRebookDialogData {
  reservation: ReservationItem;
  showOptions: RebookShowOption[];
}

@Component({
  selector: 'app-reservation-rebook-modal',
  standalone: true,
  imports: [CommonModule, MatDialogModule, MatIconModule],
  templateUrl: './reservation-rebook-modal.component.html',
  styleUrl: './reservation-rebook-modal.component.scss'
})
export class ReservationRebookModalComponent {
  isShowDropdownOpen = false;
  isBlockDropdownOpen = false;
  isAreaDropdownOpen = false;
  isTableDropdownOpen = false;

  selectedShowValue: string | null = null;
  selectedBlockValue: string | null = null;
  selectedAreaId: string | null = null;
  selectedTableNumber: number | null = null;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: ReservationRebookDialogData,
    private readonly dialogRef: MatDialogRef<ReservationRebookModalComponent>
  ) { }

  @HostListener('document:click')
  closeAllDropdowns(): void {
    this.isShowDropdownOpen = false;
    this.isBlockDropdownOpen = false;
    this.isAreaDropdownOpen = false;
    this.isTableDropdownOpen = false;
  }

  close(): void {
    this.dialogRef.close();
  }

  toggleShowDropdown(event: MouseEvent): void {
    event.stopPropagation();
    this.isShowDropdownOpen = !this.isShowDropdownOpen;
    this.isBlockDropdownOpen = false;
    this.isAreaDropdownOpen = false;
    this.isTableDropdownOpen = false;
  }

  toggleBlockDropdown(event: MouseEvent): void {
    if (!this.selectedShow) {
      return;
    }

    event.stopPropagation();
    this.isBlockDropdownOpen = !this.isBlockDropdownOpen;
    this.isShowDropdownOpen = false;
    this.isAreaDropdownOpen = false;
    this.isTableDropdownOpen = false;
  }

  toggleAreaDropdown(event: MouseEvent): void {
    if (!this.selectedBlock) {
      return;
    }

    event.stopPropagation();
    this.isAreaDropdownOpen = !this.isAreaDropdownOpen;
    this.isShowDropdownOpen = false;
    this.isBlockDropdownOpen = false;
    this.isTableDropdownOpen = false;
  }

  toggleTableDropdown(event: MouseEvent): void {
    if (!this.selectedArea) {
      return;
    }

    event.stopPropagation();
    this.isTableDropdownOpen = !this.isTableDropdownOpen;
    this.isShowDropdownOpen = false;
    this.isBlockDropdownOpen = false;
    this.isAreaDropdownOpen = false;
  }

  selectShow(option: RebookShowOption): void {
    this.selectedShowValue = option.value;
    this.selectedBlockValue = null;
    this.selectedAreaId = null;
    this.selectedTableNumber = null;
    this.isShowDropdownOpen = false;
  }

  selectBlock(option: RebookBlockOption): void {
    this.selectedBlockValue = option.value;
    this.selectedAreaId = null;
    this.selectedTableNumber = null;
    this.isBlockDropdownOpen = false;
  }

  selectArea(option: RebookAreaOption): void {
    this.selectedAreaId = option.areaId;
    this.selectedTableNumber = null;
    this.isAreaDropdownOpen = false;
  }

  selectTable(option: RebookTableOption): void {
    this.selectedTableNumber = option.tableNumber;
    this.isTableDropdownOpen = false;
  }

  get selectedShow(): RebookShowOption | undefined {
    return this.data.showOptions.find(option => option.value === this.selectedShowValue);
  }

  get selectedBlock(): RebookBlockOption | undefined {
    return this.selectedShow?.blocks.find(option => option.value === this.selectedBlockValue);
  }

  get selectedArea(): RebookAreaOption | undefined {
    return this.selectedBlock?.areas.find(option => option.areaId === this.selectedAreaId);
  }

  get availableAreas(): RebookAreaOption[] {
    return (this.selectedBlock?.areas ?? []).filter(area =>
      area.tables.some(table => this.isTableAvailable(table))
    );
  }

  get availableTables(): RebookTableOption[] {
    return (this.selectedArea?.tables ?? []).filter(table => this.isTableAvailable(table));
  }

  get selectedTable(): RebookTableOption | undefined {
    return this.availableTables.find(table => table.tableNumber === this.selectedTableNumber);
  }

  get canSubmit(): boolean {
    return !!this.selectedShow && !!this.selectedBlock && !!this.selectedArea && !!this.selectedTable;
  }

  get targetPlaceType(): number {
    return this.data.reservation.targetPlaceType ?? 0;
  }

  get requiredPersons(): number {
    return this.data.reservation.persons;
  }

  get placeTypeLabel(): string {
    return this.targetPlaceType === 1 ? 'Stehtische' : 'Sitzbereich';
  }

  getTableFreePlaces(table: RebookTableOption): number {
    return this.targetPlaceType === 1
      ? table.freeStandingPlaces
      : table.freeSeatedPlaces;
  }

  getTableTotalPlaces(table: RebookTableOption): number {
    return this.targetPlaceType === 1
      ? table.totalStandingPlaces
      : table.totalSeatedPlaces;
  }

  isTableAvailable(table: RebookTableOption): boolean {
    return this.getTableFreePlaces(table) >= this.requiredPersons;
  }

  submit(): void {
    if (!this.canSubmit || !this.selectedShow || !this.selectedBlock || !this.selectedArea || !this.selectedTable) {
      return;
    }

    const result: ReservationRebookDialogResult = {
      processId: this.data.reservation.processId,
      showId: this.selectedShow.showId,
      areaId: this.selectedArea.areaId,
      slot: this.selectedBlock.slot,
      reservedCount: this.data.reservation.persons,
      targetPlaceType: this.targetPlaceType,
      targetTableNumber: this.selectedTable.tableNumber
    };

    this.dialogRef.close(result);
  }
}